import { useState, useEffect, useCallback, useRef } from "react";
import { IConversationMessage } from "src/domains";
import {
  queryGetConversationMessages,
  mutationConversationMessage,
  subscriptionOnConversationMessage,
  IMutationConversationMessageRequest,
  IMutationConversationMessageReadRequest,
  queryGetUnreadMessages,
  mutationConversationMessageRead,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import { atom, useRecoilState } from "recoil";
import Queue from "queue-promise";
import { EventRegister } from "react-native-event-listeners";
import { LogoutEvent } from "src/utils/event";

export const subscibedChatIdState = atom<string | undefined>({
  key: "subscibedChatIdState",
  default: undefined,
});

export const useChatMessages = (id?: string) => {
  const [items, setItems] = useState<IConversationMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [, setSubscibedChatId] = useRecoilState<string | undefined>(
    subscibedChatIdState
  );
  const isMount = useRef<boolean>(true);
  const onConversationMessageSubscription = useRef<any | undefined>(); // TODO: Replace to ZenObservable.Subscription type
  const [appState, setAppState] = useState<AppStateStatus>("active");

  const refresh = useCallback(async () => {
    if (!id || !isMount.current) {
      return;
    }
    if (isMount.current) {
      setLoading(true);
    }
    if (isMount.current) {
      setError(undefined);
    }
    try {
      const response = await queryGetConversationMessages({ id });
      if (isMount.current) {
        setItems(response.data.getConversationMessages?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (isMount.current) {
        setError(e as Error);
      }
    }
    if (isMount.current) {
      setLoading(false);
    }
  }, [id]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      setAppState(state);
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    setSubscibedChatId(id);
    return () => {
      setSubscibedChatId(undefined);
    };
  }, [id, setSubscibedChatId]);

  useEffect(() => {
    if (onConversationMessageSubscription.current) {
      onConversationMessageSubscription.current.unsubscribe();
      onConversationMessageSubscription.current = undefined;
    }
    if (id && appState === "active") {
      subscriptionOnConversationMessage({
        conversationId: id,
      })
        .then((observable) => {
          onConversationMessageSubscription.current = observable.subscribe({
            next: () => refresh(),
            error: (e) => {
              console.error(e);
            },
          });
        })
        .catch((e) => console.error(e));
      // appState
    }
    return () => {
      if (onConversationMessageSubscription.current) {
        onConversationMessageSubscription.current.unsubscribe();
        onConversationMessageSubscription.current = undefined;
      }
    };
  }, [id, appState]);

  useEffect(() => {
    refresh();
  }, [refresh, id]);

  useEffect(() => {
    isMount.current = true;
    return () => {
      isMount.current = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

const queueUnreadMessages = new Queue({
  concurrent: 10,
});

export const useChatMessagesActions = () => {
  const conversationMessage = async (
    params: IMutationConversationMessageRequest
  ) => {
    await mutationConversationMessage(params);
  };

  const conversationReadMessage = (
    params: IMutationConversationMessageReadRequest
  ) => {
    queueUnreadMessages.enqueue(() => {
      return new Promise(async function (resolve, reject) {
        try {
          await mutationConversationMessageRead(params);
          resolve(true);
        } catch (e) {
          console.log("error", e);
          reject(e);
        }
      });
    });
  };

  return {
    conversationMessage,
    conversationReadMessage,
  } as const;
};

export const useChatUnreadMessages = () => {
  const [items, setItems] = useState<IConversationMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const refresh = useCallback(async () => {
    setError(undefined);
    setLoading(true);
    try {
      const response = await queryGetUnreadMessages();
      setItems(response.data.getUnreadMessages?.items || []);
    } catch (e) {
      console.error(e);
      setError(e as Error);
    }
    setLoading(true);
  }, []);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setItems([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return [items, loading, error, refresh] as const;
};
